import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../services/axiosInterceptor.js';
import useApiData from '../../services/useApiData.js';
import SnackbarEx from '../Helpers/SnackbarEx.jsx';
import AuthenticatedAssetImage from '../Helpers/AuthenticatedAssetImage.jsx';
import './inspections.css';

const MIN_SEARCH = 2;

function heroAssetFromOption(option) {
    const photos = Array.isArray(option?.heroPhoto) ? option.heroPhoto : [];
    return photos.find((asset) => asset?.gcsObject || asset?.sourceUrl) || null;
}

/** Catalog / SKU autocomplete row — matches Catalog Item edit SKU Name dropdown. */
function renderHeroSelectOption(props, option, { primary, secondary }) {
    const { key, ...optionProps } = props;
    const hero = heroAssetFromOption(option);
    return (
        <Box
            component="li"
            key={key ?? option.id}
            {...optionProps}
            sx={{
                display: 'flex !important',
                alignItems: 'center',
                gap: 1.25,
            }}
        >
            {hero ? (
                <AuthenticatedAssetImage
                    asset={hero}
                    lazy
                    alt=""
                    placeholder=""
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#f8fafc',
                        objectFit: 'cover',
                        flexShrink: 0,
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#f1f5f9',
                        flexShrink: 0,
                    }}
                />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {primary}
                </Typography>
                {secondary ? (
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {secondary}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}

function isRuntimePickKind(kind) {
    return kind === 'listingField' || kind === 'variant' || kind === 'addToBundle';
}

/** Date catalog fields (e.g. Expiration Date) are entered by the operator at inspection time. */
function isRuntimeDateCatalogField(nodeOrName) {
    const fieldName =
        typeof nodeOrName === 'string' ? nodeOrName : nodeOrName?.catalogFieldName;
    const n = String(fieldName || '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '');
    return (
        n === 'expirationdate' ||
        n === 'dateinspected' ||
        n === 'datelisted' ||
        n === 'purchasedate'
    );
}

function needsRuntimePick(node) {
    if (!node || node.type !== 'action') return false;
    if (isRuntimePickKind(node.actionKind)) return true;
    return node.actionKind === 'catalogField' && isRuntimeDateCatalogField(node);
}

function isBadOutcome(status = '', condition = '') {
    const s = `${status} ${condition}`.toLowerCase();
    return s.includes('scrap') || s.includes('parts') || s.includes('salvage') || s.includes('waste') || s.includes('grade d');
}

function answersMatch(branchValue, answer) {
    if (typeof branchValue === 'boolean' || typeof answer === 'boolean') {
        return Boolean(branchValue) === Boolean(answer) || String(branchValue) === String(answer);
    }
    return String(branchValue) === String(answer);
}

function nextNodeId(node, answer) {
    const branch = (node.branches || []).find((b) => answersMatch(b.value, answer));
    return branch?.nextNodeId || null;
}

function formatAnswer(answer) {
    if (answer === true) return 'Yes';
    if (answer === false) return 'No';
    return String(answer);
}

function formatCatalogAssignmentValue(value) {
    if (Array.isArray(value)) return value.join(', ');
    return String(value ?? '');
}

function catalogLabel(option) {
    if (option == null) return '';
    if (typeof option === 'string') return option;
    const sku = option.sku || option.listingField || '';
    const itemId = option.itemId || '';
    if (sku && itemId && sku !== itemId) return `${sku} · ${itemId}`;
    return String(sku || itemId || option.id || '');
}

function assetKey(asset) {
    return String(asset?.gcsObject || asset?.sourceUrl || '').trim();
}

function usableAssets(list) {
    return (Array.isArray(list) ? list : []).filter(
        (asset) => asset?.gcsObject || asset?.sourceUrl
    );
}

/** Product hero first, then variant photos — de-dupe by storage key. */
function variantCarouselPhotos(variant, productHero) {
    const seen = new Set();
    const out = [];
    for (const asset of [
        ...usableAssets(variant?.product?.heroPhoto),
        ...usableAssets(productHero),
        ...usableAssets(variant?.internalPackagePhotos),
    ]) {
        const key = assetKey(asset) || `idx-${out.length}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(asset);
    }
    return out;
}

function variantCardTitle(v) {
    const sku = String(v?.internalSkuId || '').trim();
    const box = String(v?.boxValue || '').trim();
    if (sku && box) return `${sku} | ${box}`;
    if (sku) return sku;
    return String(v?.label || 'Variant').trim() || 'Variant';
}

function VariantPickCard({ variant, selected, onSelect, productHero }) {
    const photos = variantCarouselPhotos(variant, productHero);
    const title = variantCardTitle(variant);

    return (
        <button
            type="button"
            className={`insp-variant-card${selected ? ' selected' : ''}`}
            onClick={() => onSelect(variant)}
        >
            <div className="insp-variant-card-carousel">
                {photos.length > 0 ? (
                    photos.map((asset, i) => (
                        <div
                            key={assetKey(asset) || i}
                            className="insp-variant-card-carousel-slide"
                        >
                            <AuthenticatedAssetImage
                                asset={asset}
                                lazy
                                alt=""
                                placeholder=""
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                }}
                            />
                        </div>
                    ))
                ) : (
                    <div className="insp-variant-card-hero-empty" />
                )}
            </div>
            <div className="insp-variant-card-body">
                <div className="insp-variant-card-title">{title}</div>
                <div className="insp-variant-card-field">
                    <span className="insp-variant-card-label">UPC</span>
                    <span className="insp-variant-card-value">
                        {String(variant?.upc || '').trim() || '—'}
                    </span>
                </div>
                <div className="insp-variant-card-field">
                    <span className="insp-variant-card-label">Year (Approx)</span>
                    <span className="insp-variant-card-value">
                        {String(variant?.yearApprox || '').trim() || '—'}
                    </span>
                </div>
                <div className="insp-variant-card-field">
                    <span className="insp-variant-card-label">MPN</span>
                    <span className="insp-variant-card-value">
                        {String(variant?.mpn || '').trim() || '—'}
                    </span>
                </div>
                {photos.length > 1 ? (
                    <div className="insp-variant-card-photo-count">
                        {photos.length} photos · swipe
                    </div>
                ) : null}
            </div>
        </button>
    );
}

export default function InspectionWizard() {
    const [phase, setPhase] = useState('intake');
    const [itemSearch, setItemSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [product, setProduct] = useState(null);
    const [tree, setTree] = useState(null);
    const [matchedBy, setMatchedBy] = useState(null);
    const [currentNodeId, setCurrentNodeId] = useState(null);
    const [pathTaken, setPathTaken] = useState([]);
    const [outcome, setOutcome] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [skuPickNodeId, setSkuPickNodeId] = useState(null);
    const [runtimePickKind, setRuntimePickKind] = useState(null);
    const [skuPickSearch, setSkuPickSearch] = useState('');
    const [debouncedSkuPick, setDebouncedSkuPick] = useState('');
    const [selectedReassignSku, setSelectedReassignSku] = useState(null);
    const [variantPickValue, setVariantPickValue] = useState('');
    const [datePickValue, setDatePickValue] = useState('');
    const [bundleProductPick, setBundleProductPick] = useState(null);
    const [bundleParents, setBundleParents] = useState([]);
    const [loadingBundleParents, setLoadingBundleParents] = useState(false);
    const [overrideOpen, setOverrideOpen] = useState(false);
    const [noTreeOpen, setNoTreeOpen] = useState(false);
    const [noTreeMessage, setNoTreeMessage] = useState('');
    const [overrideReason, setOverrideReason] = useState('');
    const [overrideCondition, setOverrideCondition] = useState('');
    const [overrideStatus, setOverrideStatus] = useState('');
    const [stepIndex, setStepIndex] = useState(1);
    const [confirmReassign, setConfirmReassign] = useState(null);
    const [snack, setSnack] = useState({ open: false, message: '', color: 'success' });

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(itemSearch.trim()), 250);
        return () => clearTimeout(t);
    }, [itemSearch]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSkuPick(skuPickSearch.trim()), 250);
        return () => clearTimeout(t);
    }, [skuPickSearch]);

    const canSearch = debouncedSearch.length >= MIN_SEARCH;
    const catalogEndpoint = useMemo(
        () =>
            `api/broylesllc/inspection-trees/meta/catalog?search=${encodeURIComponent(debouncedSearch)}&limit=50`,
        [debouncedSearch]
    );
    const { data: catalogData, load: loadingCatalog, error: catalogError } = useApiData(
        catalogEndpoint,
        { enabled: canSearch && phase === 'intake', keepPrevious: false }
    );

    const canSearchSkuPick = debouncedSkuPick.length >= MIN_SEARCH;
    const skuPickEndpoint = useMemo(
        () =>
            `api/broylesllc/inspection-trees/meta/skus?search=${encodeURIComponent(debouncedSkuPick)}&limit=50&page=1`,
        [debouncedSkuPick]
    );
    const { data: skuPickData, load: loadingSkuPick } = useApiData(skuPickEndpoint, {
        enabled:
            canSearchSkuPick &&
            (phase === 'confirm-sku' ||
                (phase === 'runtime-pick' &&
                    (runtimePickKind === 'listingField' ||
                        runtimePickKind === 'addToBundle'))),
        keepPrevious: false,
    });
    const skuPickOptions = useMemo(() => {
        if (!canSearchSkuPick) return [];
        if (Array.isArray(skuPickData?.items)) return skuPickData.items;
        if (Array.isArray(skuPickData)) return skuPickData;
        return [];
    }, [skuPickData, canSearchSkuPick]);

    const productRecordId = String(product?.productRecordId || '').trim();
    const variantCardsEndpoint = useMemo(
        () =>
            productRecordId
                ? `api/broylesllc/sku-metrics?productId=${encodeURIComponent(productRecordId)}`
                : '',
        [productRecordId]
    );
    const { data: variantCardsData, load: loadingVariantCards } = useApiData(
        variantCardsEndpoint,
        {
            enabled:
                Boolean(productRecordId) &&
                phase === 'runtime-pick' &&
                runtimePickKind === 'variant',
            keepPrevious: false,
        }
    );
    const variantCards = useMemo(
        () => (Array.isArray(variantCardsData?.items) ? variantCardsData.items : []),
        [variantCardsData]
    );

    // Ignore cached/previous query results when the search box is empty — otherwise
    // reset-to-intake after save can auto-restart the just-finished SKU.
    const catalogOptions = useMemo(
        () => (canSearch && Array.isArray(catalogData) ? catalogData : []),
        [catalogData, canSearch]
    );

    // Scanner-friendly: auto-select when exactly one match (Shelf Inventory pattern)
    useEffect(() => {
        if (phase !== 'intake' || !canSearch || loadingCatalog || catalogOptions.length !== 1) return;
        const only = catalogOptions[0];
        if (selectedItem?.id === only.id) return;
        setSelectedItem(only);
        setItemSearch('');
        startWithItem(only);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogOptions, loadingCatalog, phase, canSearch]);

    const currentNode = tree?.nodes?.[currentNodeId];

    const pathLabels = useMemo(() => {
        const confirmed = product?.sku
            ? [
                  {
                      n: 1,
                      prompt: 'Confirm product',
                      answer: confirmReassign?.sku
                          ? `Reassigned to ${confirmReassign.sku}`
                          : `Yes — ${product.sku}`,
                  },
              ]
            : [];
        if (!tree) return confirmed;
        return [
            ...confirmed,
            ...pathTaken.map((step, i) => {
                const node = tree.nodes[step.nodeId];
                const prompt =
                    step.kind === 'catalogField'
                        ? step.fieldName || node?.catalogFieldName || node?.title || 'Date'
                        : node?.prompt || node?.title || step.nodeId;
                return {
                    n: confirmed.length + i + 1,
                    prompt,
                    answer: formatAnswer(step.answer),
                };
            }),
        ];
    }, [pathTaken, tree, product, confirmReassign]);

    const resetToIntake = () => {
        setPhase('intake');
        setItemSearch('');
        setDebouncedSearch('');
        setSelectedItem(null);
        setProduct(null);
        setTree(null);
        setMatchedBy(null);
        setCurrentNodeId(null);
        setPathTaken([]);
        setOutcome(null);
        setAssignments([]);
        setSkuPickNodeId(null);
        setRuntimePickKind(null);
        setSkuPickSearch('');
        setDebouncedSkuPick('');
        setSelectedReassignSku(null);
        setVariantPickValue('');
        setDatePickValue('');
        setBundleProductPick(null);
        setBundleParents([]);
        setConfirmReassign(null);
        setStepIndex(1);
        setOverrideReason('');
        setOverrideCondition('');
        setOverrideStatus('');
        setOverrideOpen(false);
        setNoTreeOpen(false);
        setSubmitting(false);
        setLoading(false);
    };

    const startWithItem = async (item) => {
        if (!item) return;
        setLoading(true);
        try {
            const { data } = await api.post('/api/broylesllc/inspections/start', {
                catalogRecordId: item.id,
                sku: item.sku || item.listingField,
            });
            setProduct(data.product);
            setTree(data.tree);
            setMatchedBy(data.matchedBy);
            setPathTaken([]);
            setOutcome(null);
            setAssignments([]);
            setConfirmReassign(null);
            setSelectedItem(item);
            setCurrentNodeId(null);
            setStepIndex(1);
            setNoTreeOpen(false);
            setPhase('confirm-product');
        } catch (err) {
            const body = err?.response?.data;
            if (body?.code === 'no_tree') {
                setProduct(body.product || {
                    sku: item.sku,
                    category: item.category,
                    description: catalogLabel(item),
                    recordId: item.id,
                    itemId: item.itemId,
                    productRecordId: item.productRecordId,
                });
                setTree(null);
                setMatchedBy(null);
                setSelectedItem(item);
                setConfirmReassign(null);
                setPathTaken([]);
                setCurrentNodeId(null);
                setPhase('confirm-product');
                setNoTreeMessage(
                    body.message ||
                        'No inspection tree is published for this SKU or category.'
                );
            } else {
                setSnack({
                    open: true,
                    message: body?.message || 'Could not start inspection',
                    color: 'error',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const confirmProductPriorAssignments = () => {
        if (!confirmReassign?.sku && !confirmReassign?.productRecordId) return [];
        return [
            {
                kind: 'listingField',
                value: confirmReassign.sku || '',
                productRecordId: confirmReassign.productRecordId || null,
                nodeId: '__confirm_product__',
                result: {},
            },
        ];
    };

    const beginInspectionTree = (nextTree = tree, prior = confirmProductPriorAssignments()) => {
        if (!nextTree?.startNodeId) {
            setSnack({
                open: true,
                message: 'This tree has no start question.',
                color: 'warning',
            });
            return;
        }
        enterFromNode(nextTree, nextTree.startNodeId, prior);
    };

    const confirmProductYes = () => {
        if (!tree) {
            setNoTreeOpen(true);
            return;
        }
        beginInspectionTree();
    };

    const confirmProductNo = () => {
        setSkuPickSearch('');
        setSelectedReassignSku(null);
        setPhase('confirm-sku');
    };

    const applyConfirmedSku = async (skuOption) => {
        if (!skuOption?.sku) return;
        setLoading(true);
        setSelectedReassignSku(skuOption);
        try {
            const { data } = await api.post('/api/broylesllc/inspections/start', {
                catalogRecordId: product?.recordId || selectedItem?.id || undefined,
                sku: skuOption.sku,
                productRecordId: skuOption.id || undefined,
            });
            setProduct({
                ...data.product,
                recordId: product?.recordId || data.product.recordId,
                itemId: product?.itemId || data.product.itemId,
                sku: skuOption.sku,
                listingField: skuOption.sku,
                productRecordId: skuOption.id || data.product.productRecordId,
                category: skuOption.category || data.product.category,
                description: skuOption.sku,
            });
            setTree(data.tree);
            setMatchedBy(data.matchedBy);
            setConfirmReassign({
                sku: skuOption.sku,
                productRecordId: skuOption.id || null,
                category: skuOption.category || data.product.category || null,
            });
            setPathTaken([]);
            setOutcome(null);
            setAssignments([]);
            setSkuPickSearch('');
            setSelectedReassignSku(null);
            setPhase('confirm-product');
        } catch (err) {
            const body = err?.response?.data;
            if (body?.code === 'no_tree') {
                setNoTreeMessage(
                    body.message ||
                        'No inspection tree is published for this SKU or category.'
                );
                setNoTreeOpen(true);
            } else {
                setSnack({
                    open: true,
                    message: body?.message || 'Could not load tree for that SKU',
                    color: 'error',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const collectFromNode = (n) => {
        if (!n) return null;
        if (n.type === 'terminal') {
            const grade = n.result?.productGrade || n.result?.condition || n.title || '';
            if (!grade) return null;
            return { kind: 'productGrade', value: grade, result: n.result || {} };
        }
        if (n.type === 'action') {
            const kind = n.actionKind || 'productGrade';
            if (kind === 'subflow') {
                return {
                    kind,
                    value: n.subflowTreeId || '',
                    subflowTreeId: n.subflowTreeId || '',
                    result: n.result || {},
                };
            }
            if (kind === 'catalogField') {
                const fieldName = String(n.catalogFieldName || '').trim();
                // Date fields are collected via runtime pick — skip preset drain here
                if (isRuntimeDateCatalogField(fieldName)) return null;
                const fieldValue = n.value;
                const hasValue = Array.isArray(fieldValue)
                    ? fieldValue.length > 0
                    : Boolean(String(fieldValue ?? '').trim());
                if (!fieldName || !hasValue) return null;
                return {
                    kind,
                    fieldName,
                    value: fieldValue,
                    result: n.result || {},
                };
            }
            if (kind === 'shelf') {
                const shelfId = String(n.shelfId || '').trim();
                if (!shelfId) return null;
                return {
                    kind,
                    shelfId,
                    shelfBinId: String(n.shelfBinId || '').trim() || null,
                    shelfName: String(n.shelfName || n.title || '').trim(),
                    result: n.result || {},
                };
            }
            const value = n.value || n.result?.productGrade || n.result?.condition || n.title || '';
            if (!value) return null;
            return { kind, value, result: n.result || {} };
        }
        return null;
    };

    /** Rebuild catalog/result assignments from answered path only (no stale drain from a prior branch). */
    const rebuildAssignmentsFromPath = (nextTree, pathSteps, prior = confirmProductPriorAssignments()) => {
        const nodes = nextTree?.nodes || {};
        let collected = [...prior];
        const drainSoft = (fromId) => {
            let id = fromId;
            while (id && nodes[id]?.type === 'action') {
                const n = nodes[id];
                if (needsRuntimePick(n)) return id;
                const a = collectFromNode(n);
                if (a) collected.push({ ...a, nodeId: id });
                if (n.actionKind === 'subflow' || !n.nextNodeId) return id;
                id = n.nextNodeId;
            }
            return id;
        };
        let cursor = drainSoft(nextTree?.startNodeId);
        for (const step of pathSteps || []) {
            if (isRuntimePickKind(step?.kind) || step?.kind === 'catalogField') {
                const n = nodes[step.nodeId];
                if (n && (n.actionKind === step.kind || (step.kind === 'catalogField' && n.actionKind === 'catalogField'))) {
                    if (step.kind === 'catalogField') {
                        collected.push({
                            kind: 'catalogField',
                            fieldName: String(n.catalogFieldName || step.fieldName || '').trim(),
                            value: step.answer,
                            nodeId: step.nodeId,
                            result: n.result || {},
                        });
                    } else {
                        const value =
                            step.kind === 'variant'
                                ? String(step.variantId || step.answer || '')
                                : step.answer;
                        collected.push({
                            kind: step.kind,
                            value,
                            label: step.kind === 'variant' ? String(step.answer || '') : undefined,
                            productRecordId: step.productRecordId || null,
                            bundleCatalogItemId: step.bundleCatalogItemId || null,
                            createNew: Boolean(step.createNew),
                            nodeId: step.nodeId,
                            result: n.result || {},
                        });
                    }
                    cursor = n.nextNodeId ? drainSoft(n.nextNodeId) : step.nodeId;
                }
                continue;
            }
            const n = nodes[cursor];
            if (!n || n.type !== 'question') break;
            const nextId = nextNodeId(n, step.answer);
            if (!nextId) break;
            cursor = drainSoft(nextId);
        }
        return { assignments: collected, currentId: cursor };
    };

    const beginRuntimePick = (nodeId, kind) => {
        setCurrentNodeId(nodeId);
        setSkuPickNodeId(nodeId);
        setRuntimePickKind(kind);
        setSkuPickSearch('');
        setSelectedReassignSku(null);
        setVariantPickValue('');
        setDatePickValue('');
        setBundleProductPick(null);
        setBundleParents([]);
        setPhase('runtime-pick');
    };

    const drainActions = (nodes, startId, priorAssignments = []) => {
        let currentId = startId;
        const collected = [...priorAssignments];
        while (currentId && nodes[currentId]?.type === 'action') {
            const n = nodes[currentId];
            if (needsRuntimePick(n)) {
                const already = collected.find(
                    (a) =>
                        a.nodeId === currentId &&
                        (a.kind === n.actionKind ||
                            (n.actionKind === 'catalogField' && a.kind === 'catalogField'))
                );
                if (!already) {
                    // Legacy trees may still have a preset value — use it; otherwise pause for operator
                    const preset = String(n.value || '').trim();
                    if (preset && n.actionKind === 'variant') {
                        collected.push({
                            kind: 'variant',
                            value: preset,
                            nodeId: currentId,
                            result: n.result || {},
                        });
                    } else {
                        return {
                            currentId,
                            assignments: collected,
                            needsRuntimePick: true,
                            pickKind: n.actionKind,
                        };
                    }
                }
                if (!n.nextNodeId) {
                    return { currentId, assignments: collected, ended: true };
                }
                currentId = n.nextNodeId;
                continue;
            }
            const a = collectFromNode(n);
            if (a) collected.push({ ...a, nodeId: currentId });
            if (n.actionKind === 'subflow' && n.subflowTreeId) {
                return { currentId, assignments: collected, handoff: true, subflowTreeId: n.subflowTreeId };
            }
            if (!n.nextNodeId) {
                return { currentId, assignments: collected, ended: true };
            }
            currentId = n.nextNodeId;
        }
        if (currentId && nodes[currentId]?.type === 'terminal') {
            const a = collectFromNode(nodes[currentId]);
            if (a) collected.push({ ...a, nodeId: currentId });
            return { currentId, assignments: collected, ended: true };
        }
        return { currentId, assignments: collected, ended: false };
    };

    const enterFromNode = (nextTree, startId, priorAssignments = []) => {
        const drained = drainActions(nextTree.nodes, startId, priorAssignments);
        setAssignments(drained.assignments);
        setStepIndex(1);
        if (drained.needsRuntimePick) {
            beginRuntimePick(drained.currentId, drained.pickKind);
            return;
        }
        if (drained.handoff && drained.subflowTreeId) {
            startSubflow(drained.subflowTreeId, drained.assignments);
            return;
        }
        if (drained.ended) {
            finishWithAssignments(drained.assignments, drained.currentId);
            return;
        }
        setCurrentNodeId(drained.currentId);
        setPhase('step');
    };

    const finishWithAssignments = (collected, endId) => {
        const grade = [...collected].reverse().find((a) => a.kind === 'productGrade');
        const listing = [...collected].reverse().find((a) => a.kind === 'listingField');
        const bundle = [...collected].reverse().find((a) => a.kind === 'addToBundle');
        const variantAssignment = [...collected].reverse().find((a) => a.kind === 'variant');
        setAssignments(collected);
        setOutcome({
            ...(grade?.result || {}),
            condition: grade?.value || '',
            productGrade: grade?.value || '',
            variant: variantAssignment?.label || variantAssignment?.value || '',
            listingField: listing?.value || '',
            reassignedProductId: listing?.productRecordId || null,
            addToBundle: bundle
                ? {
                      sku: bundle.value || '',
                      createNew: Boolean(bundle.createNew),
                      bundleCatalogItemId: bundle.bundleCatalogItemId || null,
                  }
                : null,
            assignments: collected,
        });
        setCurrentNodeId(endId);
        setPhase('outcome');
    };

    const startSubflow = async (subflowTreeId, carriedAssignments) => {
        try {
            const { data } = await api.get(`/api/broylesllc/inspection-trees/${subflowTreeId}`);
            if (data.status !== 'published') {
                setSnack({
                    open: true,
                    message: 'Linked wizard is not published.',
                    color: 'warning',
                });
                finishWithAssignments(carriedAssignments, currentNodeId);
                return;
            }
            setTree(data);
            setMatchedBy('subflow');
            setPathTaken([]);
            setSnack({
                open: true,
                message: `Continuing in wizard: ${data.name}`,
                color: 'info',
            });
            enterFromNode(data, data.startNodeId, carriedAssignments);
        } catch {
            setSnack({ open: true, message: 'Could not load linked wizard', color: 'error' });
            finishWithAssignments(carriedAssignments, currentNodeId);
        }
    };

    const applyAnswer = (answer) => {
        const node = currentNode;
        if (!node || !tree) return;
        const nextId = nextNodeId(node, answer);
        if (!nextId || !tree.nodes[nextId]) {
            setSnack({ open: true, message: 'This answer has no next step configured.', color: 'warning' });
            return;
        }
        const nextPath = [
            ...pathTaken,
            { nodeId: currentNodeId, answer, timestamp: new Date().toISOString() },
        ];
        setPathTaken(nextPath);

        // Rebuild from path so Back + re-answer never keeps drained fields from a prior branch
        const { assignments: prior } = rebuildAssignmentsFromPath(tree, pathTaken);
        const drained = drainActions(tree.nodes, nextId, prior);
        setAssignments(drained.assignments);

        if (drained.needsRuntimePick) {
            beginRuntimePick(drained.currentId, drained.pickKind);
            return;
        }
        if (drained.handoff && drained.subflowTreeId) {
            startSubflow(drained.subflowTreeId, drained.assignments);
            return;
        }
        if (drained.ended) {
            finishWithAssignments(drained.assignments, drained.currentId);
            return;
        }

        setCurrentNodeId(drained.currentId);
        setStepIndex((s) => s + 1);
    };

    const continueAfterRuntimePick = (nextAssignments, fromNodeId) => {
        const node = tree?.nodes?.[fromNodeId];
        const continueFrom = node?.nextNodeId || '';
        setSkuPickNodeId(null);
        setRuntimePickKind(null);
        setSelectedReassignSku(null);
        setSkuPickSearch('');
        setVariantPickValue('');
        setDatePickValue('');

        if (!continueFrom) {
            finishWithAssignments(nextAssignments, fromNodeId);
            return;
        }

        const drained = drainActions(tree.nodes, continueFrom, nextAssignments);
        setAssignments(drained.assignments);

        if (drained.needsRuntimePick) {
            beginRuntimePick(drained.currentId, drained.pickKind);
            return;
        }
        if (drained.handoff && drained.subflowTreeId) {
            startSubflow(drained.subflowTreeId, drained.assignments);
            return;
        }
        if (drained.ended) {
            finishWithAssignments(drained.assignments, drained.currentId);
            return;
        }
        setCurrentNodeId(drained.currentId);
        setPhase('step');
        setStepIndex((s) => s + 1);
    };

    const confirmSkuReassign = (skuOption) => {
        if (!skuOption?.sku || !tree || !skuPickNodeId) return;
        const node = tree.nodes[skuPickNodeId];
        const nextPath = [
            ...pathTaken,
            {
                nodeId: skuPickNodeId,
                answer: skuOption.sku,
                productRecordId: skuOption.id || null,
                kind: 'listingField',
                timestamp: new Date().toISOString(),
            },
        ];
        setPathTaken(nextPath);

        const assignment = {
            kind: 'listingField',
            value: skuOption.sku,
            productRecordId: skuOption.id || null,
            nodeId: skuPickNodeId,
            result: node?.result || {},
        };
        const nextAssignments = [
            ...assignments.filter((a) => !(a.kind === 'listingField' && a.nodeId === skuPickNodeId)),
            assignment,
        ];
        continueAfterRuntimePick(nextAssignments, skuPickNodeId);
    };

    const confirmVariantPick = (raw, displayLabel = '') => {
        const value = String(raw ?? '').trim();
        if (!value || !tree || !skuPickNodeId) return;
        const node = tree.nodes[skuPickNodeId];
        const label = String(displayLabel || '').trim() || value;
        setVariantPickValue(value);
        const nextPath = [
            ...pathTaken,
            {
                nodeId: skuPickNodeId,
                answer: label,
                variantId: value,
                kind: 'variant',
                timestamp: new Date().toISOString(),
            },
        ];
        setPathTaken(nextPath);

        const assignment = {
            kind: 'variant',
            value,
            label,
            nodeId: skuPickNodeId,
            result: node?.result || {},
        };
        const nextAssignments = [
            ...assignments.filter((a) => !(a.kind === 'variant' && a.nodeId === skuPickNodeId)),
            assignment,
        ];
        continueAfterRuntimePick(nextAssignments, skuPickNodeId);
    };

    const selectVariantCard = (variant) => {
        if (!variant?._id) return;
        confirmVariantPick(String(variant._id), variantCardTitle(variant));
    };

    const confirmCatalogDatePick = (raw) => {
        const value = String(raw ?? '').trim();
        if (!value || !tree || !skuPickNodeId) return;
        const node = tree.nodes[skuPickNodeId];
        const fieldName = String(node?.catalogFieldName || 'Expiration Date').trim();
        const nextPath = [
            ...pathTaken,
            {
                nodeId: skuPickNodeId,
                answer: value,
                fieldName,
                kind: 'catalogField',
                timestamp: new Date().toISOString(),
            },
        ];
        setPathTaken(nextPath);

        const assignment = {
            kind: 'catalogField',
            fieldName,
            value,
            nodeId: skuPickNodeId,
            result: node?.result || {},
        };
        const nextAssignments = [
            ...assignments.filter(
                (a) => !(a.kind === 'catalogField' && a.nodeId === skuPickNodeId)
            ),
            assignment,
        ];
        continueAfterRuntimePick(nextAssignments, skuPickNodeId);
    };

    const loadBundleParentsForProduct = async (skuOption) => {
        if (!skuOption?.id && !skuOption?.sku) return;
        setBundleProductPick(skuOption);
        setLoadingBundleParents(true);
        setBundleParents([]);
        try {
            const productKey = skuOption.id || skuOption.sku;
            const { data } = await api.get(
                `/api/broylesllc/catalog-items/bundle-parents?product=${encodeURIComponent(productKey)}`
            );
            setBundleParents(Array.isArray(data?.parents) ? data.parents : []);
            setPhase('bundle-target');
        } catch (err) {
            setSnack({
                open: true,
                color: 'error',
                message:
                    err?.response?.data?.message ||
                    'Could not load bundle parents for that SKU.',
            });
        } finally {
            setLoadingBundleParents(false);
        }
    };

    const confirmAddToBundle = ({ createNew = false, parent = null } = {}) => {
        if (!tree || !skuPickNodeId || !bundleProductPick) return;
        if (!createNew && !parent?.id) return;
        const node = tree.nodes[skuPickNodeId];
        const sku =
            bundleProductPick.sku ||
            bundleProductPick.listingField ||
            '';
        const nextPath = [
            ...pathTaken,
            {
                nodeId: skuPickNodeId,
                answer: sku,
                productRecordId: bundleProductPick.id || null,
                kind: 'addToBundle',
                createNew: Boolean(createNew),
                bundleCatalogItemId: createNew ? null : parent.id,
                bundleItemId: createNew ? null : parent.itemId || '',
                timestamp: new Date().toISOString(),
            },
        ];
        setPathTaken(nextPath);

        const assignment = {
            kind: 'addToBundle',
            value: sku,
            productRecordId: bundleProductPick.id || null,
            createNew: Boolean(createNew),
            bundleCatalogItemId: createNew ? null : parent.id,
            nodeId: skuPickNodeId,
            result: node?.result || {},
        };
        const nextAssignments = [
            ...assignments.filter(
                (a) => !(a.kind === 'addToBundle' && a.nodeId === skuPickNodeId)
            ),
            assignment,
        ];
        setBundleProductPick(null);
        setBundleParents([]);
        continueAfterRuntimePick(nextAssignments, skuPickNodeId);
    };

    const goBack = () => {
        if (phase === 'confirm-sku') {
            setSelectedReassignSku(null);
            setSkuPickSearch('');
            setPhase('confirm-product');
            return;
        }
        if (phase === 'confirm-product') {
            resetToIntake();
            return;
        }
        if (phase === 'bundle-target') {
            setBundleParents([]);
            setSelectedReassignSku(null);
            setSkuPickSearch('');
            setPhase('runtime-pick');
            return;
        }
        if (phase === 'runtime-pick') {
            setSkuPickNodeId(null);
            setRuntimePickKind(null);
            setSelectedReassignSku(null);
            setSkuPickSearch('');
            setVariantPickValue('');
            setDatePickValue('');
            setBundleProductPick(null);
            setBundleParents([]);
            setOutcome(null);
            if (!tree || pathTaken.length === 0) {
                setAssignments(confirmProductPriorAssignments());
                setPhase('confirm-product');
                setStepIndex(1);
                return;
            }
            const last = pathTaken[pathTaken.length - 1];
            const nextPath = pathTaken.slice(0, -1);
            setPathTaken(nextPath);
            const rebuilt = rebuildAssignmentsFromPath(tree, nextPath);
            setAssignments(rebuilt.assignments);
            setCurrentNodeId(last.nodeId);
            setPhase('step');
            setStepIndex(Math.max(1, nextPath.length + 1));
            return;
        }
        if (phase === 'outcome') {
            if (!tree || pathTaken.length === 0) {
                setOutcome(null);
                setAssignments(confirmProductPriorAssignments());
                setPhase('confirm-product');
                setStepIndex(1);
                return;
            }
            const last = pathTaken[pathTaken.length - 1];
            const nextPath = pathTaken.slice(0, -1);
            setPathTaken(nextPath);
            setOutcome(null);
            const rebuilt = rebuildAssignmentsFromPath(tree, nextPath);
            setAssignments(rebuilt.assignments);
            if (isRuntimePickKind(last.kind) || last.kind === 'catalogField') {
                beginRuntimePick(last.nodeId, last.kind);
                if (last.kind === 'listingField' || last.kind === 'addToBundle') {
                    setSkuPickSearch(String(last.answer || ''));
                } else if (last.kind === 'catalogField') {
                    setDatePickValue(String(last.answer || ''));
                } else {
                    setVariantPickValue(String(last.variantId || last.answer || ''));
                }
                setStepIndex(Math.max(1, nextPath.length + 1));
                return;
            }
            setCurrentNodeId(last.nodeId);
            setPhase('step');
            setStepIndex(Math.max(1, nextPath.length));
            return;
        }
        if (pathTaken.length === 0) {
            if (tree && product) {
                setCurrentNodeId(null);
                setOutcome(null);
                setAssignments(confirmProductPriorAssignments());
                setPhase('confirm-product');
                setStepIndex(1);
                return;
            }
            resetToIntake();
            return;
        }
        const last = pathTaken[pathTaken.length - 1];
        const nextPath = pathTaken.slice(0, -1);
        setPathTaken(nextPath);
        if (tree) {
            const rebuilt = rebuildAssignmentsFromPath(tree, nextPath);
            setAssignments(rebuilt.assignments);
        }
        if (isRuntimePickKind(last.kind) || last.kind === 'catalogField') {
            beginRuntimePick(last.nodeId, last.kind);
            if (last.kind === 'listingField' || last.kind === 'addToBundle') {
                setSkuPickSearch(String(last.answer || ''));
            } else if (last.kind === 'catalogField') {
                setDatePickValue(String(last.answer || ''));
            } else {
                setVariantPickValue(String(last.variantId || last.answer || ''));
            }
            return;
        }
        setCurrentNodeId(last.nodeId);
        setStepIndex(Math.max(1, nextPath.length));
    };

    const submit = async (withOverride = false) => {
        setSubmitting(true);
        try {
            const payload = {
                sku: product.sku,
                treeId: tree._id,
                treeVersion: tree.version,
                pathTaken,
                outcome,
                catalogRecordId: product.recordId,
                productRecordId: product.productRecordId || undefined,
                reassignedProductId: confirmReassign?.productRecordId || undefined,
                listingField: confirmReassign?.sku || undefined,
            };
            if (withOverride) {
                payload.override = {
                    reason: overrideReason,
                    condition: overrideCondition || undefined,
                    status: overrideStatus || undefined,
                };
            }
            const { data } = await api.post('/api/broylesllc/inspections/submit', payload);
            resetToIntake();
            setSnack({
                open: true,
                message: data.message || 'Saved',
                color: data.synced || data.airtableSynced ? 'success' : 'info',
            });
        } catch (err) {
            setSnack({
                open: true,
                message: err?.response?.data?.message || 'Submit failed',
                color: 'error',
            });
            setSubmitting(false);
        }
    };

    const openOverride = () => {
        setOverrideCondition(outcome?.condition || '');
        setOverrideStatus(outcome?.status || '');
        setOverrideOpen(true);
    };

    return (
        <div className="insp-page">
            <div className="insp-page-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h1>Inspection Wizard</h1>
                        <p className="insp-sub">
                            Operator wizard
                            {tree ? ` · ${tree.name}` : ''}
                            {matchedBy ? ` · matched by ${matchedBy}` : ''}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {tree && (
                            <>
                                <span className="insp-badge" style={{ background: '#f3f4f6' }}>
                                    v{tree.version}
                                </span>
                                <span className={`insp-badge ${tree.status}`}>{tree.status}</span>
                            </>
                        )}
                        {phase !== 'intake' && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RefreshOutlinedIcon />}
                                onClick={resetToIntake}
                            >
                                New item
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="insp-body">
                {phase === 'intake' && (
                    <div className="insp-wizard-center" style={{ maxWidth: 560 }}>
                        <div className="icon-circle">
                            <QrCodeScannerOutlinedIcon fontSize="large" />
                        </div>
                        <h2 style={{ margin: '0 0 8px' }}>Scan or search Item ID / SKU</h2>
                        <p className="insp-meta" style={{ marginBottom: 20 }}>
                            Looks up Item ID Catalog, then loads a published tree for that SKU, or its AZ
                            Category if no SKU tree exists. Exact single matches auto-select (scanner-friendly).
                        </p>
                        <Autocomplete
                            options={catalogOptions ?? []}
                            loading={loadingCatalog || loading}
                            value={selectedItem}
                            inputValue={itemSearch ?? ''}
                            onChange={(_, value) => {
                                setSelectedItem(value);
                                if (value) startWithItem(value);
                            }}
                            onInputChange={(_, value, reason) => {
                                if (reason === 'reset') return;
                                setItemSearch(String(value ?? ''));
                            }}
                            getOptionLabel={catalogLabel}
                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                            filterOptions={(x) => (Array.isArray(x) ? x : [])}
                            autoHighlight
                            openOnFocus
                            noOptionsText={
                                itemSearch.trim().length < MIN_SEARCH
                                    ? `Type or scan at least ${MIN_SEARCH} characters`
                                    : loadingCatalog
                                      ? 'Searching…'
                                      : catalogError
                                        ? 'Unable to load catalog'
                                        : 'No items found'
                            }
                            renderOption={(props, option) =>
                                renderHeroSelectOption(props, option, {
                                    primary: catalogLabel(option),
                                    secondary: [
                                        option.itemId ? `Item ID ${option.itemId}` : null,
                                        option.macItemSubId
                                            ? `MAC Item Sub ID ${option.macItemSubId}`
                                            : null,
                                        option.category || 'No AZ Category',
                                    ]
                                        .filter(Boolean)
                                        .join(' · '),
                                })
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    placeholder="Scan barcode or type Item ID / Listing Field / MAC Item Sub ID"
                                    sx={{ mb: 2, background: '#fff' }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {(loadingCatalog || loading) && (
                                                    <CircularProgress color="inherit" size={20} />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Button
                            variant="text"
                            disabled={loading}
                            onClick={() =>
                                startWithItem({
                                    id: null,
                                    sku: 'HEADPHONES-BLK-01',
                                    listingField: 'HEADPHONES-BLK-01',
                                    category: 'Electronics',
                                })
                            }
                        >
                            Use sample SKU (HEADPHONES-BLK-01)
                        </Button>
                    </div>
                )}

                {phase === 'confirm-product' && product && (
                    <div className="insp-wizard-panel">
                        <div className="insp-wizard-toolbar">
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={goBack}
                                className="insp-back-btn"
                            >
                                Back
                            </Button>
                            <span className="insp-badge draft">Confirm product</span>
                        </div>

                        <div className="insp-item-card">
                            <div className="label">SCANNED ITEM</div>
                            {product.itemId && (
                                <div className="insp-meta" style={{ marginBottom: 4 }}>
                                    Item ID · {product.itemId}
                                </div>
                            )}
                            <div style={{ fontWeight: 700, fontSize: '1.15rem' }}>
                                {product.sku || product.listingField || 'Unknown SKU'}
                            </div>
                            <div className="insp-meta">
                                {product.category || 'No AZ Category'}
                                {product.productGrade ? ` · Grade ${product.productGrade}` : ''}
                            </div>
                            {confirmReassign?.sku && (
                                <div className="insp-meta" style={{ marginTop: 8, fontWeight: 600 }}>
                                    SKU reassigned — will update SKU Name on confirm.
                                </div>
                            )}
                        </div>

                        <h2 style={{ fontSize: '1.2rem', margin: '0 0 8px' }}>
                            Is this the correct product?
                        </h2>
                        <p className="insp-meta" style={{ marginBottom: 16 }}>
                            This step is always first. If the listing is wrong (misbox), choose No and
                            pick the correct SKU.
                            {!tree
                                ? ' No published tree is linked to this SKU yet — reassign the SKU or publish a tree in Tree Builder.'
                                : ''}
                        </p>

                        <button
                            type="button"
                            className="insp-choice-btn"
                            disabled={loading}
                            onClick={confirmProductYes}
                        >
                            Yes — continue inspection
                        </button>
                        <button
                            type="button"
                            className="insp-choice-btn"
                            disabled={loading}
                            onClick={confirmProductNo}
                        >
                            No — choose a different SKU
                        </button>
                    </div>
                )}

                {phase === 'confirm-sku' && (
                    <div className="insp-wizard-panel">
                        <div className="insp-wizard-toolbar">
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={goBack}
                                className="insp-back-btn"
                            >
                                Back
                            </Button>
                            <span className="insp-badge draft">Correct SKU</span>
                        </div>

                        <div className="insp-item-card">
                            <div className="label">CURRENT LISTING</div>
                            <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{product?.sku}</div>
                            <div className="insp-meta">
                                {product?.itemId ? `Item ID ${product.itemId}` : product?.description}
                                {product?.category ? ` · ${product.category}` : ''}
                            </div>
                        </div>

                        <h2 style={{ fontSize: '1.2rem', margin: '0 0 8px' }}>
                            Assign the correct SKU
                        </h2>
                        <p className="insp-meta" style={{ marginBottom: 16 }}>
                            Search Products and pick the Listing Field this Item ID should link to.
                        </p>

                        <Autocomplete
                            options={skuPickOptions}
                            loading={loadingSkuPick || loading}
                            value={selectedReassignSku}
                            inputValue={skuPickSearch}
                            onChange={(_, value) => {
                                setSelectedReassignSku(value);
                                if (value) applyConfirmedSku(value);
                            }}
                            onInputChange={(_, value, reason) => {
                                if (reason === 'reset') return;
                                setSkuPickSearch(String(value ?? ''));
                            }}
                            getOptionLabel={(option) =>
                                typeof option === 'string'
                                    ? option
                                    : String(option?.sku || option?.listingField || '')
                            }
                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                            filterOptions={(x) => (Array.isArray(x) ? x : [])}
                            autoHighlight
                            openOnFocus
                            noOptionsText={
                                skuPickSearch.trim().length < MIN_SEARCH
                                    ? `Type at least ${MIN_SEARCH} characters`
                                    : loadingSkuPick
                                      ? 'Searching…'
                                      : 'No SKUs found'
                            }
                            renderOption={(props, option) =>
                                renderHeroSelectOption(props, option, {
                                    primary: option.sku || option.listingField || '—',
                                    secondary: [
                                        option.internalSkuId,
                                        option.category || option.azCategory || 'No AZ Category',
                                    ]
                                        .filter(Boolean)
                                        .join(' · '),
                                })
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    placeholder="Search Listing Field / SKU"
                                    sx={{ mb: 2, background: '#fff' }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {(loadingSkuPick || loading) && (
                                                    <CircularProgress color="inherit" size={20} />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </div>
                )}

                {phase === 'step' && currentNode && (
                    <div className="insp-wizard-panel">
                        <div className="insp-wizard-toolbar">
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={goBack}
                                className="insp-back-btn"
                            >
                                Back
                            </Button>
                            <span className="insp-badge draft">Step {stepIndex}</span>
                        </div>

                        <div className="insp-item-card">
                            <div className="label">ITEM</div>
                            <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{product?.sku}</div>
                            <div className="insp-meta">
                                {product?.description}
                                {product?.category ? ` · ${product.category}` : ''}
                            </div>
                        </div>

                        <h2 style={{ fontSize: '1.2rem', margin: '0 0 16px' }}>{currentNode.prompt}</h2>

                        {currentNode.inputType === 'boolean' ? (
                            <>
                                <button type="button" className="insp-choice-btn" onClick={() => applyAnswer(true)}>
                                    Yes
                                </button>
                                <button type="button" className="insp-choice-btn" onClick={() => applyAnswer(false)}>
                                    No
                                </button>
                            </>
                        ) : (
                            (currentNode.options || currentNode.branches?.map((b) => b.value) || []).map((opt) => (
                                <button
                                    key={String(opt)}
                                    type="button"
                                    className="insp-choice-btn"
                                    onClick={() => applyAnswer(opt)}
                                >
                                    {String(opt)}
                                </button>
                            ))
                        )}

                        <div className="insp-path-so-far">
                            <div className="title">PATH SO FAR</div>
                            {pathLabels.length === 0 ? (
                                <span className="insp-meta">—</span>
                            ) : (
                                pathLabels.map((p) => (
                                    <div key={p.n} className="insp-meta">
                                        {p.n}. {formatAnswer(p.answer)}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {phase === 'runtime-pick' && (
                    <div
                        className={`insp-wizard-panel${
                            runtimePickKind === 'variant' ? ' insp-wizard-panel--variants' : ''
                        }`}
                    >
                        <div className="insp-wizard-toolbar">
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={goBack}
                                className="insp-back-btn"
                            >
                                Back
                            </Button>
                            <span className="insp-badge draft">
                                {runtimePickKind === 'variant'
                                    ? 'Set Variant'
                                    : runtimePickKind === 'addToBundle'
                                      ? 'Add to Bundle'
                                      : runtimePickKind === 'catalogField'
                                        ? tree?.nodes?.[skuPickNodeId]?.catalogFieldName ||
                                          'Enter Date'
                                        : 'Correct SKU'}
                            </span>
                        </div>

                        <div className="insp-item-card">
                            <div className="label">CURRENT ITEM</div>
                            <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{product?.sku}</div>
                            <div className="insp-meta">
                                {product?.description}
                                {product?.category ? ` · ${product.category}` : ''}
                            </div>
                        </div>

                        <h2 style={{ fontSize: '1.2rem', margin: '0 0 8px' }}>
                            {runtimePickKind === 'catalogField'
                                ? tree?.nodes?.[skuPickNodeId]?.catalogFieldName ||
                                  tree?.nodes?.[skuPickNodeId]?.title ||
                                  'Enter date'
                                : tree?.nodes?.[skuPickNodeId]?.title ||
                                  (runtimePickKind === 'variant'
                                      ? "Set Item ID's Variant"
                                      : runtimePickKind === 'addToBundle'
                                        ? 'Choose bundle Product SKU'
                                        : 'Assign correct SKU')}
                        </h2>
                        <p className="insp-meta" style={{ marginBottom: 16 }}>
                            {runtimePickKind === 'variant'
                                ? 'Tap a variant card to write it to the catalog item.'
                                : runtimePickKind === 'addToBundle'
                                  ? 'Search Products for the bundle SKU (e.g. pallet). Next you will pick an existing bundle or create a new one.'
                                  : runtimePickKind === 'catalogField'
                                    ? 'Enter the date from the product at inspection — this is written to the catalog item on save.'
                                    : 'Search Products and pick the SKU this Item ID should link to (SKU Name).'}
                        </p>

                        {runtimePickKind === 'catalogField' ? (
                            <>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label={
                                        tree?.nodes?.[skuPickNodeId]?.catalogFieldName ||
                                        'Expiration Date'
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    value={datePickValue}
                                    onChange={(e) => setDatePickValue(e.target.value)}
                                    sx={{ mb: 2, background: '#fff' }}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && datePickValue.trim()) {
                                            e.preventDefault();
                                            confirmCatalogDatePick(datePickValue);
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    disabled={!datePickValue.trim()}
                                    onClick={() => confirmCatalogDatePick(datePickValue)}
                                >
                                    Continue
                                </Button>
                            </>
                        ) : runtimePickKind === 'variant' ? (
                            !productRecordId ? (
                                <p className="insp-meta">
                                    This item has no linked Product, so variants cannot be loaded.
                                </p>
                            ) : loadingVariantCards ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : variantCards.length === 0 ? (
                                <p className="insp-meta">
                                    No variants found for this Product yet.
                                </p>
                            ) : (
                                <div className="insp-variant-card-grid">
                                    {variantCards.map((v) => (
                                        <VariantPickCard
                                            key={v._id}
                                            variant={v}
                                            productHero={product?.heroPhoto}
                                            selected={
                                                String(variantPickValue) === String(v._id)
                                            }
                                            onSelect={selectVariantCard}
                                        />
                                    ))}
                                </div>
                            )
                        ) : (
                            <Autocomplete
                                options={skuPickOptions}
                                loading={loadingSkuPick || loadingBundleParents}
                                value={selectedReassignSku}
                                inputValue={skuPickSearch}
                                onChange={(_, value) => {
                                    setSelectedReassignSku(value);
                                    if (!value) return;
                                    if (runtimePickKind === 'addToBundle') {
                                        loadBundleParentsForProduct(value);
                                    } else {
                                        confirmSkuReassign(value);
                                    }
                                }}
                                onInputChange={(_, value, reason) => {
                                    if (reason === 'reset') return;
                                    setSkuPickSearch(String(value ?? ''));
                                }}
                                getOptionLabel={(option) =>
                                    typeof option === 'string'
                                        ? option
                                        : String(option?.sku || option?.listingField || '')
                                }
                                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                                filterOptions={(x) => (Array.isArray(x) ? x : [])}
                                autoHighlight
                                openOnFocus
                                noOptionsText={
                                    skuPickSearch.trim().length < MIN_SEARCH
                                        ? `Type at least ${MIN_SEARCH} characters`
                                        : loadingSkuPick
                                          ? 'Searching…'
                                          : 'No SKUs found'
                                }
                                renderOption={(props, option) =>
                                    renderHeroSelectOption(props, option, {
                                        primary: option.sku || option.listingField || '—',
                                        secondary: [
                                            option.internalSkuId,
                                            option.category || option.azCategory || 'No AZ Category',
                                        ]
                                            .filter(Boolean)
                                            .join(' · '),
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        autoFocus
                                        placeholder="Search Listing Field / SKU"
                                        sx={{ mb: 2, background: '#fff' }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {(loadingSkuPick || loadingBundleParents) && (
                                                        <CircularProgress color="inherit" size={20} />
                                                    )}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}

                    </div>
                )}

                {phase === 'bundle-target' && (
                    <div className="insp-wizard-panel">
                        <div className="insp-wizard-toolbar">
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={goBack}
                                className="insp-back-btn"
                            >
                                Back
                            </Button>
                            <span className="insp-badge draft">Choose Bundle</span>
                        </div>

                        <div className="insp-item-card">
                            <div className="label">CURRENT ITEM</div>
                            <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{product?.sku}</div>
                            <div className="insp-meta">
                                Bundle Product:{' '}
                                {bundleProductPick?.sku ||
                                    bundleProductPick?.listingField ||
                                    '—'}
                            </div>
                        </div>

                        <h2 style={{ fontSize: '1.2rem', margin: '0 0 8px' }}>
                            Add to which bundle?
                        </h2>

                        {bundleParents.length === 0 ? (
                            <p className="insp-meta" style={{ marginBottom: 16 }}>
                                No existing bundle catalog items for this Product SKU. A new{' '}
                                <strong>bundl_…</strong> item will be created and this unit will be
                                added to it.
                            </p>
                        ) : (
                            <p className="insp-meta" style={{ marginBottom: 16 }}>
                                Pick an existing bundle parent, or create a new one for this SKU.
                            </p>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {bundleParents.map((parent) => (
                                <Button
                                    key={parent.id}
                                    variant="outlined"
                                    onClick={() =>
                                        confirmAddToBundle({ createNew: false, parent })
                                    }
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textTransform: 'none',
                                        py: 1.25,
                                    }}
                                >
                                    <Box sx={{ textAlign: 'left' }}>
                                        <div style={{ fontWeight: 700 }}>
                                            {parent.itemId || parent.id}
                                        </div>
                                        <div className="insp-meta">
                                            {parent.status || '—'} · {parent.sourceCount || 0}{' '}
                                            source
                                            {(parent.sourceCount || 0) === 1 ? '' : 's'}
                                        </div>
                                    </Box>
                                </Button>
                            ))}
                            <Button
                                variant="contained"
                                onClick={() => confirmAddToBundle({ createNew: true })}
                            >
                                {bundleParents.length
                                    ? 'Create new bundle for this SKU'
                                    : 'Create new bundle and continue'}
                            </Button>
                        </div>
                    </div>
                )}

                {phase === 'outcome' && outcome && (
                    <div className="insp-wizard-panel">
                        <div className="insp-wizard-toolbar">
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={goBack}
                                className="insp-back-btn"
                            >
                                Back
                            </Button>
                        </div>
                        <div className="label">OUTCOME RESOLVED</div>
                        <h2 style={{ margin: '4px 0 16px', fontSize: '1.5rem' }}>{product?.sku}</h2>

                        <div
                            className={`insp-outcome-card ${
                                isBadOutcome(outcome.status, outcome.condition) ? 'bad' : 'good'
                            }`}
                        >
                            <span
                                className={`insp-badge ${
                                    isBadOutcome(outcome.status, outcome.condition)
                                        ? 'archived'
                                        : 'published'
                                }`}
                                style={
                                    isBadOutcome(outcome.status, outcome.condition)
                                        ? { background: '#C13515', color: '#fff' }
                                        : { background: '#008A05', color: '#fff' }
                                }
                            >
                                {outcome.status || 'Outcome'}
                            </span>
                            {outcome.condition && (
                                <div style={{ fontWeight: 700, fontSize: '1.25rem', marginTop: 10 }}>
                                    Grade: {outcome.condition}
                                </div>
                            )}
                            {outcome.variant && (
                                <div style={{ fontWeight: 650, marginTop: 8 }}>
                                    Variant: {outcome.variant}
                                </div>
                            )}
                            {outcome.listingField && (
                                <div style={{ fontWeight: 650, marginTop: 8 }}>
                                    Reassign SKU to: {outcome.listingField}
                                </div>
                            )}
                            {outcome.addToBundle && (
                                <div style={{ fontWeight: 650, marginTop: 8 }}>
                                    {outcome.addToBundle.createNew
                                        ? `Create new bundle under SKU: ${outcome.addToBundle.sku}`
                                        : `Add to existing bundle for SKU: ${outcome.addToBundle.sku}`}
                                </div>
                            )}
                            {assignments
                                .filter((a) => a.kind === 'catalogField')
                                .map((a, i) => (
                                    <div key={`catalog-${i}`} style={{ fontWeight: 650, marginTop: 8 }}>
                                        {a.fieldName}: {formatCatalogAssignmentValue(a.value)}
                                    </div>
                                ))}
                            {assignments
                                .filter((a) => a.kind === 'shelf')
                                .map((a, i) => (
                                    <div key={`shelf-${i}`} style={{ fontWeight: 650, marginTop: 8 }}>
                                        Shelf: {a.shelfName || a.shelfId || '—'}
                                    </div>
                                ))}
                            {outcome.instructions && (
                                <div style={{ marginTop: 8 }}>{outcome.instructions}</div>
                            )}
                            {outcome.requiresPhotos && (
                                <div
                                    style={{
                                        marginTop: 12,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        fontWeight: 600,
                                    }}
                                >
                                    <PhotoCameraOutlinedIcon fontSize="small" />
                                    Photos required before submit
                                </div>
                            )}
                        </div>

                        <div className="insp-decision-path">
                            <div
                                className="title"
                                style={{
                                    fontWeight: 700,
                                    marginBottom: 8,
                                    fontSize: '0.7rem',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                DECISION PATH ({pathLabels.length} answers)
                            </div>
                            {pathLabels.map((p) => (
                                <div key={p.n} style={{ marginBottom: 6 }}>
                                    {p.n}.{' '}
                                    {String(p.prompt || '').length > 48
                                        ? `${String(p.prompt).slice(0, 48)}…`
                                        : p.prompt || '—'}
                                    {' → '}
                                    <strong>{p.answer}</strong>
                                </div>
                            ))}
                        </div>

                        <div className="insp-wizard-actions">
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={submitting}
                                onClick={() => submit(false)}
                                sx={{ py: 1.3 }}
                            >
                                {submitting ? (
                                    <CircularProgress size={22} color="inherit" />
                                ) : (
                                    'Confirm & save to catalog'
                                )}
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                disabled={submitting}
                                onClick={openOverride}
                            >
                                Flag override…
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={noTreeOpen} onClose={() => setNoTreeOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>No inspection tree available</DialogTitle>
                <DialogContent>
                    <p style={{ marginTop: 0 }}>{noTreeMessage}</p>
                    {product && (
                        <p className="insp-meta">
                            Item: <strong>{product.sku || product.description}</strong>
                            {product.category ? ` · AZ Category: ${product.category}` : ''}
                        </p>
                    )}
                    <p className="insp-meta">
                        Create and publish a tree for this SKU or category in Tree Builder, then try again.
                    </p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNoTreeOpen(false)}>Close</Button>
                    <Button
                        variant="contained"
                        component={RouterLink}
                        to="/inspections/trees"
                        onClick={() => setNoTreeOpen(false)}
                    >
                        Open Tree Builder
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={overrideOpen} onClose={() => setOverrideOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Flag override</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        required
                        label="Reason"
                        margin="dense"
                        multiline
                        minRows={2}
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Override Product Grade"
                        margin="dense"
                        value={overrideCondition}
                        onChange={(e) => setOverrideCondition(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Override status"
                        margin="dense"
                        value={overrideStatus}
                        onChange={(e) => setOverrideStatus(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOverrideOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!overrideReason.trim() || submitting}
                        onClick={() => submit(true)}
                    >
                        Confirm override & sync
                    </Button>
                </DialogActions>
            </Dialog>

            <SnackbarEx
                open={snack.open}
                message={snack.message}
                color={snack.color === 'info' ? 'info' : snack.color}
                close={() => setSnack((s) => ({ ...s, open: false }))}
            />
        </div>
    );
}
